import * as i0 from '@angular/core';
import { Injectable, Component, InjectionToken, Optional, Inject, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { filter, finalize } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

class LoaderService {
    constructor() {
        /** Keep track of all pending loaders. Helps prevent a faster response from hiding the spinner before slower responses */
        this._pending = [];
        this._loading$ = new BehaviorSubject(false);
        /** Observable that emits true if any requests are still pending */
        this.loading$ = this._loading$.asObservable();
    }
    /** Start or stop the spinner by setting loading to true/false */
    set loading(pending) {
        if (pending) {
            this._pending.push(pending);
        }
        else {
            this._pending.pop();
        }
        this._loading$.next(this._pending.length > 0);
    }
}
LoaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LoaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class LoaderComponent {
    constructor(_loader, _cd) {
        this._loader = _loader;
        this._cd = _cd;
        this.loading = false;
    }
    ngOnInit() {
        this._cd.detach();
        this._loader.loading$
            .pipe(filter((loading) => this.loading !== loading))
            .subscribe((loading) => {
            this.loading = loading;
            this._cd.detectChanges();
        });
    }
}
LoaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderComponent, deps: [{ token: LoaderService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
LoaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: LoaderComponent, selector: "ps-ui-loader", ngImport: i0, template: `<div id="loader-wrapper" *ngIf="loading">
    <div id="loader"><span></span></div>
  </div>`, isInline: true, styles: ["#loader-wrapper{position:fixed;top:0;left:0;width:100%;height:100%;z-index:9999;background:rgba(255,255,255,.7)}#loader{display:block;position:relative;left:50%;top:50%;width:150px;height:150px;margin:-75px 0 0 -75px;border-radius:50%;border:3px solid transparent;border-top-color:#d33831;animation:spin 2s linear infinite}#loader span{content:\"\";position:absolute;top:25px;left:25px;right:25px;bottom:25px;border-radius:50%;border:3px solid transparent;border-top-color:#e1cb51;animation:spin 2.5s linear infinite}#loader:before{content:\"\";position:absolute;top:5px;left:5px;right:5px;bottom:5px;border-radius:50%;border:3px solid transparent;border-top-color:#4a91c5;animation:spin 3s linear infinite}#loader:after{content:\"\";position:absolute;top:15px;left:15px;right:15px;bottom:15px;border-radius:50%;border:3px solid transparent;border-top-color:#3f7e42;animation:spin 1.5s linear infinite}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-loader', template: `<div id="loader-wrapper" *ngIf="loading">
    <div id="loader"><span></span></div>
  </div>`, styles: ["#loader-wrapper{position:fixed;top:0;left:0;width:100%;height:100%;z-index:9999;background:rgba(255,255,255,.7)}#loader{display:block;position:relative;left:50%;top:50%;width:150px;height:150px;margin:-75px 0 0 -75px;border-radius:50%;border:3px solid transparent;border-top-color:#d33831;animation:spin 2s linear infinite}#loader span{content:\"\";position:absolute;top:25px;left:25px;right:25px;bottom:25px;border-radius:50%;border:3px solid transparent;border-top-color:#e1cb51;animation:spin 2.5s linear infinite}#loader:before{content:\"\";position:absolute;top:5px;left:5px;right:5px;bottom:5px;border-radius:50%;border:3px solid transparent;border-top-color:#4a91c5;animation:spin 3s linear infinite}#loader:after{content:\"\";position:absolute;top:15px;left:15px;right:15px;bottom:15px;border-radius:50%;border:3px solid transparent;border-top-color:#3f7e42;animation:spin 1.5s linear infinite}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] }]
        }], ctorParameters: function () { return [{ type: LoaderService }, { type: i0.ChangeDetectorRef }]; } });

/**
 * Automatically start the spinner on the provided observable and stop it when the observable completes or errors
 */
function withLoader(loader) {
    return (source) => {
        loader.loading = true;
        return source.pipe(finalize(() => (loader.loading = false)));
    };
}

const LOADER_URLS = new InjectionToken('LOADER_URLS');
const EXCLUDE_URLS = new InjectionToken('EXCLUDE_URLS');
class LoaderInterceptor {
    constructor(_loader, _loaderUrls, _excludeUrls) {
        this._loader = _loader;
        this._loaderUrls = _loaderUrls;
        this._excludeUrls = _excludeUrls;
    }
    intercept(req, next) {
        var _a;
        if (!((_a = this._loaderUrls) === null || _a === void 0 ? void 0 : _a.length) ||
            this._urlIsExcluded(req.url) ||
            !this._urlIsIncluded(req.url)) {
            return next.handle(req);
        }
        return next.handle(req).pipe(withLoader(this._loader));
    }
    _urlIsIncluded(url) {
        return this._urlMatchesList(url, this._loaderUrls);
    }
    _urlIsExcluded(url) {
        return this._urlMatchesList(url, this._excludeUrls);
    }
    _urlMatchesList(url, list = []) {
        return list.some((listUrl) => new RegExp(listUrl).test(url));
    }
}
LoaderInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderInterceptor, deps: [{ token: LoaderService }, { token: LOADER_URLS, optional: true }, { token: EXCLUDE_URLS, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
LoaderInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: LoaderService }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [LOADER_URLS]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [EXCLUDE_URLS]
                    }] }];
    } });

class LoaderModule {
    static forRoot(config) {
        return {
            ngModule: LoaderModule,
            providers: [
                { provide: LOADER_URLS, useValue: config.loaderUrls || [] },
                { provide: EXCLUDE_URLS, useValue: config.excludeUrls || [] },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: LoaderInterceptor,
                    multi: true,
                },
            ],
        };
    }
}
LoaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LoaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderModule, declarations: [LoaderComponent], imports: [CommonModule], exports: [LoaderComponent] });
LoaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [LoaderComponent],
                    exports: [LoaderComponent],
                    imports: [CommonModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EXCLUDE_URLS, LOADER_URLS, LoaderComponent, LoaderInterceptor, LoaderModule, LoaderService, withLoader };
